import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../config';
import { DB_COLLECTIONS } from '../databaseStructure';
import { Mail, Lock, User as UserIcon, ShoppingBag, Calendar, Package, Download } from 'lucide-react';
import './ProfilePage.css';

const ProfilePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, signIn, signUp, signInWithGoogle, userProfile } = useAuth();
  const { addToCart } = useCart();
  
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    displayName: ''
  });
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ordersLoading, setOrdersLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadOrders();
    } else {
      setOrdersLoading(false);
    }
  }, [user]);

  const loadOrders = async () => {
    if (!user) return;
    
    setOrdersLoading(true);
    try {
      const ordersRef = collection(db, DB_COLLECTIONS.ORDERS);
      const q = query(
        ordersRef,
        where('userId', '==', user.uid),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const ordersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate()
      }));
      
      setOrders(ordersData);
    } catch (err) {
      console.error('Error loading orders:', err);
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        await signIn(formData.email, formData.password);
      } else {
        await signUp(formData.email, formData.password, formData.displayName);
      }
      
      const redirect = searchParams.get('redirect');
      if (redirect) {
        navigate(`/${redirect}`);
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);
    
    try {
      await signInWithGoogle();
      const redirect = searchParams.get('redirect');
      if (redirect) {
        navigate(`/${redirect}`);
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleRepurchase = (order) => {
    order.items.forEach(item => {
      addToCart({
        id: item.productId,
        title: item.productTitle,
        artist: item.artistName,
        price: item.price,
        type: item.type,
        image: item.image || 'https://via.placeholder.com/400x533'
      }, item.quantity);
    });
    navigate('/cart');
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered':
        return 'status-delivered';
      case 'paid':
      case 'processing':
        return 'status-processing';
      case 'shipped':
        return 'status-shipped';
      case 'cancelled':
        return 'status-cancelled';
      default:
        return 'status-pending';
    }
  };

  if (!user) {
    return (
      <div className="profile-page">
        <div className="container">
          <div className="auth-container">
            <div className="auth-card">
              <h1 className="auth-title">
                {isLogin ? 'Welcome Back' : 'Create Account'}
              </h1>
              <p className="auth-subtitle">
                {isLogin 
                  ? 'Sign in to view your orders and continue shopping' 
                  : 'Join TRCKLST to start collecting amazing posters'}
              </p>

              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="auth-form">
                {!isLogin && (
                  <div className="form-group">
                    <label>
                      <UserIcon size={20} />
                      <input
                        type="text"
                        placeholder="Display Name"
                        value={formData.displayName}
                        onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                        required
                      />
                    </label>
                  </div>
                )}

                <div className="form-group">
                  <label>
                    <Mail size={20} />
                    <input
                      type="email"
                      placeholder="Email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </label>
                </div>

                <div className="form-group">
                  <label>
                    <Lock size={20} />
                    <input
                      type="password"
                      placeholder="Password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      minLength="6"
                    />
                  </label>
                </div>

                <button 
                  type="submit" 
                  className="submit-btn"
                  disabled={loading}
                >
                  {loading ? 'Processing...' : (isLogin ? 'Sign In' : 'Create Account')}
                </button>
              </form>

              <div className="divider">
                <span>or</span>
              </div>

              <button 
                onClick={handleGoogleSignIn}
                className="google-btn"
                disabled={loading}
              >
                <svg width="20" height="20" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </button>

              <p className="toggle-text">
                {isLogin ? "Don't have an account? " : 'Already have an account? '}
                <button 
                  onClick={() => setIsLogin(!isLogin)}
                  className="toggle-btn"
                >
                  {isLogin ? 'Sign Up' : 'Sign In'}
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="container">
        <div className="profile-header">
          <div className="user-info">
            <div className="user-avatar">
              {userProfile?.photoURL ? (
                <img src={userProfile.photoURL} alt={userProfile.displayName} />
              ) : (
                <UserIcon size={32} />
              )}
            </div>
            <div>
              <h1 className="user-name">
                {userProfile?.displayName || user.email}
              </h1>
              <p className="user-email">{user.email}</p>
            </div>
          </div>

          <div className="user-stats">
            <div className="stat-card">
              <ShoppingBag size={24} />
              <div>
                <div className="stat-value">{orders.length}</div>
                <div className="stat-label">Orders</div>
              </div>
            </div>
            <div className="stat-card">
              <Package size={24} />
              <div>
                <div className="stat-value">
                  {orders.reduce((sum, order) => sum + order.items.length, 0)}
                </div>
                <div className="stat-label">Posters</div>
              </div>
            </div>
          </div>
        </div>

        <div className="orders-section">
          <h2 className="section-title">
            <span className="gradient-text">Order</span> History
          </h2>

          {ordersLoading ? (
            <div className="loading-orders">
              <p>Loading your orders...</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="no-orders">
              <div className="no-orders-icon">
                <ShoppingBag size={48} />
              </div>
              <h3>No orders yet</h3>
              <p>Start building your poster collection today!</p>
              <button 
                className="shop-btn"
                onClick={() => navigate('/shop')}
              >
                Browse Posters
              </button>
            </div>
          ) : (
            <div className="orders-list">
              {orders.map((order) => (
                <div key={order.id} className="order-card">
                  <div className="order-header">
                    <div className="order-info">
                      <div className="order-number">
                        Order #{order.orderNumber}
                      </div>
                      <div className="order-date">
                        <Calendar size={16} />
                        {formatDate(order.createdAt)}
                      </div>
                    </div>
                    <div className={`order-status ${getStatusColor(order.status)}`}>
                      {order.status}
                    </div>
                  </div>

                  <div className="order-items">
                    {order.items.map((item, index) => (
                      <div key={index} className="order-item">
                        <div className="item-info">
                          <div className="item-name">{item.productTitle}</div>
                          <div className="item-artist">by {item.artistName}</div>
                        </div>
                        <div className="item-quantity">x{item.quantity}</div>
                        <div className="item-price">${item.price.toFixed(2)}</div>
                      </div>
                    ))}
                  </div>

                  <div className="order-footer">
                    <div className="order-total">
                      Total: <span>${order.total.toFixed(2)}</span>
                    </div>
                    <div className="order-actions">
                      {order.digitalDelivered && (
                        <button className="action-btn download-btn">
                          <Download size={16} />
                          Download
                        </button>
                      )}
                      <button 
                        className="action-btn repurchase-btn"
                        onClick={() => handleRepurchase(order)}
                      >
                        <ShoppingBag size={16} />
                        Repurchase
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;